import {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {CONVERSION_LOST_REASONS, ConversionCta, ConversionCtaNeeds} from '../../domain/entities/ConversionPipeline.ts'
import {useCrmMutations} from '../../hooks/usePipelines.ts'
import {crmErrorMessage, ctaLabel, newCommandId} from './pipelineView.ts'
import {FormDialog, MarkLostDialog} from './PipelineDialogs.tsx'
import {PendingReasonDialog, SentToPrintDialog, ValueDialog} from './ConversionDialogs.tsx'

// Where each form's value goes in the command data.
const NEEDS_FIELD: Record<ConversionCtaNeeds, string> = {
    pendingReason: 'reason',
    printChannel: 'printChannel',
    trackingUrl: 'trackingUrl',
    lostReason: 'reason',
}

type CtaRun = {id: string; cta: ConversionCta; data: Record<string, unknown>; note?: string}

const runKey = (id: string, cta: ConversionCta) => `${id}:${cta.key}`

// Runs a server CTA: its form first (`needs`), then the "sends a message"
// confirmation, then the command. A straight or confirmed send reports errors
// as a toast; inside a form the error stays in the form (so its value is not
// lost), like the other dialogs.
export function useConversionCtaRunner() {
    const {t} = useTranslation('dashboard')
    const {sendConversionCommand} = useCrmMutations()
    const [running, setRunning] = useState<string | null>(null)
    const [needsRun, setNeedsRun] = useState<CtaRun | null>(null)
    const [confirmRun, setConfirmRun] = useState<CtaRun | null>(null)
    // Set when the form was submitted and the confirmation comes next.
    const advanced = useRef<CtaRun | null>(null)

    const send = ({id, cta, data, note}: CtaRun) =>
        sendConversionCommand({
            id,
            command: {commandId: newCommandId(), type: cta.command, note: note || undefined, data: Object.keys(data).length ? data : undefined},
        }).then(() => enqueueSnackbar(t('pipelines.cta.done', {label: ctaLabel(cta, t)}), {variant: 'success'}))

    const runCta = (id: string, cta: ConversionCta) => {
        if (running) return
        const run: CtaRun = {id, cta, data: {...(cta.data ?? {})}}
        if (cta.needs && NEEDS_FIELD[cta.needs]) {
            setNeedsRun(run)
            return
        }
        if (cta.sendsMessage) {
            setConfirmRun(run)
            return
        }
        setRunning(runKey(id, cta))
        send(run)
            .catch((error) => enqueueSnackbar(crmErrorMessage(error, t), {variant: 'error'}))
            .finally(() => setRunning(null))
    }

    const submitNeeds = (value: unknown, note?: string) => {
        if (!needsRun?.cta.needs) return Promise.resolve()
        const data = {...needsRun.data}
        if (value !== undefined && value !== '') data[NEEDS_FIELD[needsRun.cta.needs]] = value
        const next: CtaRun = {...needsRun, data, note}
        if (needsRun.cta.sendsMessage) {
            advanced.current = next
            return Promise.resolve()
        }
        return send(next)
    }

    const closeNeeds = () => {
        const next = advanced.current
        advanced.current = null
        setNeedsRun(null)
        if (next) setConfirmRun(next)
    }

    const needs = needsRun?.cta.needs ?? null
    const lostRequest = useMemo(() => (needs === 'lostReason' ? {reason: null} : null), [needs])
    const shippedRequest = useMemo(() => (needs === 'trackingUrl' ? {kind: 'shipped' as const} : null), [needs])
    const confirmLabel = confirmRun ? ctaLabel(confirmRun.cta, t) : ''

    const dialogs = (
        <>
            <PendingReasonDialog isOpen={needs === 'pendingReason'} title={t('pipelines.pending.title')} submitLabel={t('pipelines.pending.submit')}
                                 onClose={closeNeeds} onSubmit={(reason, note) => submitNeeds(reason, note)}/>
            <SentToPrintDialog isOpen={needs === 'printChannel'} initial={null}
                               onClose={closeNeeds} onSubmit={(printChannel) => submitNeeds(printChannel)}/>
            <ValueDialog request={shippedRequest} onClose={closeNeeds} onSubmit={(value) => submitNeeds(value)}/>
            <MarkLostDialog request={lostRequest} allowedReasons={CONVERSION_LOST_REASONS}
                            onClose={closeNeeds} onSubmit={(reason, note) => submitNeeds(reason, note)}/>
            <FormDialog isOpen={!!confirmRun} title={t('pipelines.cta.confirmTitle')} submitLabel={confirmLabel}
                        canSubmit onClose={() => setConfirmRun(null)}
                        onSubmit={() => (confirmRun
                            ? send(confirmRun).catch((error) => enqueueSnackbar(crmErrorMessage(error, t), {variant: 'error'}))
                            : Promise.resolve())}>
                <p className="text-[13.5px] leading-[1.5] text-neutralBlack-800">{t('pipelines.cta.confirmBody', {label: confirmLabel})}</p>
            </FormDialog>
        </>
    )

    return {runCta, isRunning: (id: string, cta: ConversionCta) => running === runKey(id, cta), isBusy: !!running, dialogs}
}

export type RunConversionCta = (conversionId: string, cta: ConversionCta) => void
